import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheckIcon, GraduationCapIcon, BookOpenIcon, SearchIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
import { hasMatchParams, readMatchParams } from '../utils/matchParams';
interface HeroSectionProps {
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string) => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  const navigate = useNavigate();
  // Clean-URL form: /degree/:areaStudy/:program/:degreeInterest — the third
  // segment is optional.
  const {
    areaStudy,
    program,
    degreeInterest: degreeInterestParam
  } = useParams<{
    areaStudy?: string;
    program?: string;
    degreeInterest?: string;
  }>();
  // Anchor links elsewhere on the site carry the visitor's context in the URL
  // (?degreeInterest=&areaStudy=&mProgram=), so the widget opens pre-filled.
  const urlPrefill = readMatchParams();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState(hasMatchParams(urlPrefill) ? urlPrefill : undefined);
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program,
        // A query string still wins, so ?degreeInterest= can override the path.
        ...(degreeInterestParam && !formData.degreeInterest ? {
          degreeInterest: degreeInterestParam
        } : {})
      });
    }
  }, [areaStudy, program, degreeInterestParam, formData.areaOfStudy, formData.degreeInterest, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization);
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value);
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  // Resolve display labels for trust signals
  const selectedDegree = degreeInterestOptions.find(d => d.value === formData.degreeInterest);
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  }
  return <div className="relative bg-navy overflow-hidden" data-id="element-442" data-mp="185ymxst4tire">
      {/* Brand background image, with a light scrim so the headline keeps
          contrast over the brighter area on the right. */}
      <div className="hero-bg absolute inset-0 z-0" aria-hidden="true" data-id="element-443" data-mp="1b7ta9winelk6" />
      <div className="absolute inset-0 z-0 bg-navy-dark/20" aria-hidden="true" data-id="element-444" data-mp="1f5r0n2o5ndp5" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-445" data-mp="1k6y7mzih48ur">
        {/* On mobile the search widget sits directly under the headline, so the
            first thing in reach is the action. On desktop the copy stacks in the
            left column with the widget alongside it. */}
        <div className="py-12 md:py-16 lg:py-20 flex flex-col lg:grid lg:grid-cols-2 lg:items-center lg:gap-x-12" data-id="element-446" data-mp="whdmqb2rf1iu">
          {/* Headline */}
          <div className="order-1 lg:col-start-1 lg:row-start-1 text-center lg:text-left" data-id="element-447" data-mp="166nji48jt8da">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm font-semibold mb-5 text-white" data-id="element-448" data-mp="8r9m8ckvbhw2">
              <span className="flex h-2 w-2 rounded-full mr-2 bg-gold-light" data-id="element-449" data-mp="dp1nhx7ekgcx" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-white tracking-tight mb-6 lg:mb-5 leading-[1.15]" data-id="element-450" data-mp="64hcoasxblzx">
              {headline}
            </h1>
          </div>

          {/* Supporting copy + trust row */}
          <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 text-center lg:text-left mt-10 lg:mt-0" data-id="element-451" data-mp="3ndxbbcyji27p">
            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-452" data-mp="3jewwe5j6njpz">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm text-gray-300" data-id="element-453" data-mp="34ps9ib1g0rbj">
              <div className="flex items-center" data-id="element-454" data-mp="2zr79ocq3s9nm">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-455" data-mp="2vou5hsr08jsy">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-456" data-mp="sdnty1ic8hlo" />
                </svg>
                Flexible and Convenient
              </div>
              <div className="flex items-center" data-id="element-457" data-mp="4nth3jy0qt1f">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-458" data-mp="9fbho6w4qrlv">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-459" data-mp="3vud0hnhx7rbc" />
                </svg>
                Accredited Schools
              </div>
              <div className="flex items-center" data-id="element-460" data-mp="338eoavyo925q">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-461" data-mp="3397o1cz7qred">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-462" data-mp="3cae6vspc4eas" />
                </svg>
                No Obligation
              </div>
            </div>
          </div>

          {/* Search widget */}
          <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 flex justify-center lg:justify-end" data-id="element-463" data-mp="3uzce9s9nm8ni">
            <div className="w-full max-w-md" data-id="element-464" data-mp="3qz1yxozz25vz">
              <form onSubmit={handleContinue} className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-465" data-mp="3r0d2bnpi9bmk">
                {/* Card Header */}
                <div className="px-6 py-3.5 bg-navy" style={{
                backgroundColor: "#5FA0EE"
              }} data-id="element-466" data-mp="2qslgq2jjsmv2">
                  <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-467" data-mp="2m1woum3yhnve">
                    Search Featured Schools
                  </h2>
                </div>

                <div className="p-6 space-y-4" data-id="element-468" data-mp="2hxx7606xwj0g">
                  {/* Degree Level */}
                  <div data-id="element-469" data-mp="2cz3wefj1f63z">
                    <div className="relative" data-id="element-470" data-mp="39r7g9czf25wv">
                      <GraduationCapIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy" aria-hidden="true" data-id="element-471" data-mp="39qxobjut9ooj" />
                      <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} aria-label="Select Degree Level" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-472" data-mp="30svr7fe77g6j">
                      <option value="" data-id="element-473" data-mp="1k4patcp8g7zp">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-474" data-mp="1szc9zr4azo3i">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-475" data-mp="22v6xazjh27cm">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-476" data-mp="dibscftbjdnn">
                    <div className="relative" data-id="element-477" data-mp="dhk09eyxjy24">
                      <BookOpenIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy" aria-hidden="true" data-id="element-478" data-mp="dkfxf12yeipi" />
                      <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} aria-label="Select Area of Study" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-479" data-mp="fftqotixtbvv">
                      <option value="" data-id="element-480" data-mp="t89onfwzejpx">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-481" data-mp="177j062a8spnk">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-482" data-mp="1qt1f423cd3wr">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization */}
                  <div data-id="element-483" data-mp="1hqs4lc82pajd">
                    <div className="relative" data-id="element-484" data-mp="1hu7msnx1mzp0">
                      <SearchIcon className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${formData.areaOfStudy ? 'text-navy' : 'text-gray-400'}`} aria-hidden="true" data-id="element-485" data-mp="132yq5uuq4e2d" />
                      <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} aria-label="Select Program" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-486" data-mp="u0phlaj0murm">
                      <option value="" data-id="element-487" data-mp="18q46l6oybqtp">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-488" data-mp="uqky2cs8adgv">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-489" data-mp="2p5dozyi62g2h">
                        {errors.specialization}
                      </p>}
                  </div>

                  {/* Continue Button */}
                  <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark" data-id="element-490" data-mp="36dy6bb1tpddu">
                    Search Programs
                  </button>
                </div>

                {/* Reassurance footer — answers the three things people
                    hesitate over right before submitting. */}
                <div className="border-t border-gray-200 bg-light px-6 py-4" data-id="element-491" data-mp="2rq3ahlsiqswj">
                  <p className="flex items-center justify-center gap-2 text-xs font-semibold text-navy" data-id="element-492" data-mp="2xr3f6r8fk64">
                    <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 text-gold-dark" aria-hidden="true" data-id="element-493" data-mp="6y1l3godzz0t" />
                    Trusted by 2M+ students to find the right school
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
}